import { useState, useEffect, useMemo } from 'react';
import { X, Check, CreditCard, ArrowRight, ArrowLeft, Search, ChevronLeft, Loader2, AlertCircle, Plus, Tag, Sparkles, Coins, Clock, Landmark } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { productsApi, paymentApi, defaultsApi, transactionsApi, groupCodeApi, groupsApi, marketplaceDealsApi, wixCommentsApi } from '../../services/api';
import { bonndleAuthApi } from '../../services/bonndleAuthApi';
import { convertToLocalCurrency, getCountryCode } from '../../utils/currency';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

// Production API for Bonndle Bills page
const PROD_API_URL = 'https://boldscaleservices.com';

const prodApiFetch = async (endpoint) => {
  const response = await fetch(`${PROD_API_URL}${endpoint}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);
  return response.json();
};

const extractArray = (response) => {
  if (Array.isArray(response)) return response;
  if (response?.data && Array.isArray(response.data)) return response.data;
  if (response?.products && Array.isArray(response.products)) return response.products;
  return [];
};

// Production Products API
const prodProductsApi = {
  async getByProvider(providerUuid) {
    // Production API uses providerId param (not providerUuid)
    const response = await prodApiFetch(`/products?providerId=${providerUuid}&page=1&limit=500`);
    // API returns { popularPlans: [], all: [] } format
    if (response && response.all) {
      return response.all;
    }
    return extractArray(response);
  },
  async lookup(productId, accountNumber) {
    const response = await prodApiFetch(`/products/${productId}/lookup?accountNumber=${encodeURIComponent(accountNumber)}`);
    return response;
  }
};

const DEFAULT_CASHBACK_PERCENT = 3;

// Helper to get product category - supports both snake_case and camelCase field names
const getProductCategory = (product) => {
  return product?.category_name || product?.categoryName || product?.category || '';
};

// Helper to extract provider display name (includes country e.g. "MTN NIGERIA")
const getProviderDisplayName = (provider) => {
  if (!provider?.name) return 'Provider';
  let name = provider.name;
  const country = provider.country;

  // If country exists, format as "PROVIDER COUNTRY" (e.g. "MTN NIGERIA")
  if (country) {
    const countryUpper = country.toUpperCase();
    const nameUpper = name.toUpperCase();

    // Remove country from name if it's a prefix (e.g. "Nigeria MTN" -> "MTN")
    if (nameUpper.startsWith(countryUpper + ' ')) {
      name = name.substring(country.length + 1).trim();
    }
    // Remove country from name if it's a suffix (e.g. "MTN Nigeria" -> "MTN")
    else if (nameUpper.endsWith(' ' + countryUpper)) {
      name = name.substring(0, name.length - country.length - 1).trim();
    }

    // Return formatted as "PROVIDER COUNTRY"
    return `${name} ${countryUpper}`.toUpperCase();
  }

  return name.toUpperCase();
};

// Helper to get simplified category name for display
const getSimpleCategoryName = (categoryName) => {
  if (!categoryName) return 'Other';
  // Extract main category: "Mobile Data > Daily Bundles" -> "Mobile Data"
  // Or keep full name if short: "Mobile Top Up" -> "Mobile Top Up"
  if (categoryName.includes('>')) {
    return categoryName.split('>')[0].trim();
  }
  // Treat Mobile PIN as Airtime (same as Mobile Top Up)
  const name = categoryName.toLowerCase();
  if (name.includes('mobile pin') || name.includes('pin')) {
    return 'Mobile Top Up';
  }
  return categoryName;
};

// Helper to get short category name for switch buttons (bundle types)
const getShortCategoryName = (categoryName) => {
  if (!categoryName) return 'Other';
  const name = categoryName.toLowerCase();

  // For subcategories like "Mobile Data > Daily Bundles" - check this FIRST
  if (categoryName.includes('>')) {
    const sub = categoryName.split('>')[1].trim().toLowerCase();
    // Only 5 bundle types: Daily, Weekly, Monthly, Yearly, Special
    if (sub.includes('daily') || sub.includes('1 day') || sub.includes('24 hour')) return 'Daily';
    if (sub.includes('weekly') || sub.includes('7 day') || sub.includes('7-14') || sub.includes('week')) return 'Weekly';
    if (sub.includes('monthly') || sub.includes('30 day') || sub.includes('month')) return 'Monthly';
    if (sub.includes('yearly') || sub.includes('annual') || sub.includes('year') || sub.includes('365') || sub.includes('extended')) return 'Yearly';
    // Everything else falls under Special
    return 'Special';
  }

  // Main service types (not bundle types)
  if (name.includes('mobile top up') || name.includes('airtime')) return 'Airtime';
  if (name.includes('mobile pin') || name.includes('pin')) return 'Airtime';
  if (name.includes('mobile data')) return 'Data'; // This shouldn't show in bundle selector
  if (name.includes('bill payment')) return 'Bills';
  if (name.includes('cable') || name.includes('tv')) return 'Cable TV';
  if (name.includes('electricity') || name.includes('power')) return 'Electricity';
  if (name.includes('internet')) return 'Internet';
  if (name.includes('voucher') || name.includes('gift')) return 'Vouchers';

  return categoryName.length > 12 ? categoryName.substring(0, 10) + '...' : categoryName;
};

// Helper to detect bundle type from product name/validity (Flutter approach)
// Returns: 'daily', 'weekly', 'monthly', 'yearly', or 'special'
// IMPORTANT: Check longer duration patterns FIRST to avoid false matches
const getProductBundleType = (product) => {
  if (!product) return 'special';
  const name = (product.name || '').toLowerCase();
  const validity = (product.validity || '').toLowerCase();
  const combined = `${name} ${validity}`;

  // Yearly: 365 Days, Annual, Yearly (check FIRST - "365 days" contains "5 day")
  const yearlyPatterns = [
    '365 day', '365day', '365-day', '365days',
    '1 year', '1year', '1-year',
    'yearly', 'annual', 'annually',
    '12 month', '12month', '12-month',
  ];
  for (const pattern of yearlyPatterns) {
    if (combined.includes(pattern)) return 'yearly';
  }

  // Monthly: 30 days, 1 month, monthly (check before weekly - "30 days" could conflict)
  const monthlyPatterns = [
    '30 day', '30day', '30-day', '30days',
    '1 month', '1month', '1-month',
    'monthly', 'month',
  ];
  for (const pattern of monthlyPatterns) {
    if (combined.includes(pattern)) return 'monthly';
  }

  // Weekly: 7 days, 14 days, 15 days, Weekly (check before daily - "14/15 days" contains "4/5 day")
  const weeklyPatterns = [
    '7 day', '7day', '7-day', '7days', '1 week', '1week', 'weekly',
    '14 day', '14day', '14-day', '14days',
    '15 day', '15day', '15-day', '15days',
    '2 week', '2week',
  ];
  for (const pattern of weeklyPatterns) {
    if (combined.includes(pattern)) return 'weekly';
  }

  // Daily: 1 Day, 2 Days, 3 Days, 4 Days, 5 Days, 6 Days, Daily (check LAST)
  const dailyPatterns = [
    '1 day', '1day', '1-day', '1days',
    '2 day', '2day', '2-day', '2days',
    '3 day', '3day', '3-day', '3days',
    '4 day', '4day', '4-day', '4days',
    '5 day', '5day', '5-day', '5days',
    '6 day', '6day', '6-day', '6days',
    '24 hour', '24hour', '48 hour', '48hour', '72 hour', '72hour',
    'daily',
  ];
  for (const pattern of dailyPatterns) {
    if (combined.includes(pattern)) return 'daily';
  }

  // Special: everything else
  return 'special';
};

// Helper to get normalized main category from product category_name and product name
// Returns: 'Airtime', 'Data', or the original category
// When category is 'Uncategorized', falls back to checking product name
const getNormalizedCategory = (categoryName, productName = '') => {
  if (!categoryName) return 'Other';
  const catLower = categoryName.toLowerCase();
  const nameLower = (productName || '').toLowerCase();

  // Normalize airtime categories
  if (catLower.includes('mobile top up') || catLower.includes('airtime') ||
      catLower.includes('mobile pin') || catLower.includes('pin') || catLower.includes('recharge')) {
    return 'Airtime';
  }

  // Normalize data categories
  if (catLower.includes('mobile data') || catLower.includes('data')) {
    return 'Data';
  }

  // Extract main category before '>'
  if (categoryName.includes('>')) {
    return categoryName.split('>')[0].trim();
  }

  // If category is Uncategorized/Other, check product name for airtime/data keywords
  if (catLower === 'uncategorized' || catLower === 'other') {
    if (nameLower.includes('airtime') || nameLower.includes('topup') ||
        nameLower.includes('top up') || nameLower.includes('recharge')) {
      return 'Airtime';
    }
    if (nameLower.includes('data') || nameLower.includes('gb') || nameLower.includes('mb')) {
      return 'Data';
    }
  }

  return categoryName;
};

// Determine if category is airtime/topup type (variable amount)
const isAirtimeCategory = (categoryName) => {
  if (!categoryName) return false;
  const name = categoryName.toLowerCase();
  return name.includes('top up') || name.includes('topup') || name.includes('airtime') || name.includes('mobile pin') || name.includes('pin');
};

// Helper to get account label based on category
const getAccountLabel = (categoryName) => {
  const name = categoryName?.toLowerCase() || '';
  if (name.includes('voucher') || name.includes('gift') || name.includes('e_voucher')) return 'Recipient Email';
  if (name.includes('cable') || name.includes('tv')) return 'Smart Card';
  if (name.includes('electric') || name.includes('power')) return 'Meter No.';
  if (name.includes('data') || name.includes('top up') || name.includes('topup') || name.includes('airtime') || name.includes('pin')) return 'Phone Number';
  if (name.includes('internet')) return 'Account ID';
  if (name.includes('bet') || name.includes('gaming')) return 'User ID';
  return 'Account';
};

// Helper to get placeholder text
const getAccountPlaceholder = (categoryName) => {
  const name = categoryName?.toLowerCase() || '';
  if (name.includes('voucher') || name.includes('gift') || name.includes('e_voucher')) return 'Enter recipient email address';
  if (name.includes('cable') || name.includes('tv')) return 'Enter smart card number';
  if (name.includes('electric') || name.includes('power')) return 'Enter meter number';
  if (name.includes('data') || name.includes('top up') || name.includes('topup') || name.includes('airtime') || name.includes('pin')) return 'Enter phone number';
  if (name.includes('internet')) return 'Enter account ID';
  return 'Enter account identifier';
};

export default function PayBillSlider({ provider, onClose, cashbackPercent, promoCode: initialPromoCode, merchantLogo, hideBackdrop = false, dealGroupId, dealMarketplaceId, useProductionApi = false }) {
  const { payWithRewardsEnabled, profileForm, triggerGroupChatRefresh } = useAppState();
  const { user, rewardsData, fetchUserData } = useAuth();
  const displayName = getProviderDisplayName(provider);

  // Lock body scroll when slider is open
  useBodyScrollLock(!!provider);

  // Use merchant logo from group deal if provided, otherwise fall back to provider logo
  const logoUrl = merchantLogo || provider?.logo_url;

  // Use the deal's cashback percent if provided, otherwise default to 3%
  const CASHBACK_PERCENT = cashbackPercent || DEFAULT_CASHBACK_PERCENT;

  // Promo/Cashback code state - auto-fill if coming from group deals
  const [promoCode, setPromoCode] = useState(initialPromoCode || '');
  const [hasAutoAppliedCode, setHasAutoAppliedCode] = useState(!!initialPromoCode);

  // Update promo code when it's provided asynchronously (e.g., generated in background)
  useEffect(() => {
    if (initialPromoCode && !promoCode) {
      setPromoCode(initialPromoCode);
      setHasAutoAppliedCode(true);
    }
  }, [initialPromoCode]);

  // Get user's country code for currency conversion
  const userCountryCode = useMemo(() => {
    const countryValue = user?.country || user?.country_code || '';
    return getCountryCode(countryValue) || 'US';
  }, [user]);

  // Calculate rewards balance in local currency (use same fallbacks as HomeTab)
  // Try user object first since it's updated directly from API, then fall back to rewardsData
  const rewardsBalance = useMemo(() => {
    // Check all possible sources for the balance
    const fromUser = user?.available_bold_credits || user?.bold_credits || user?.boldCredits;
    const fromRewardsData = rewardsData?.balance;
    // Use whichever has a non-zero value
    return fromUser || fromRewardsData || 0;
  }, [user?.available_bold_credits, user?.bold_credits, user?.boldCredits, rewardsData?.balance]);

  // Convert points to USD (1000 points = $1 USD)
  const rewardsInUSD = useMemo(() => {
    return rewardsBalance / 1000;
  }, [rewardsBalance]);

  const rewardsInLocalCurrency = useMemo(() => {
    if (rewardsInUSD <= 0) return null;
    return convertToLocalCurrency(rewardsInUSD, userCountryCode);
  }, [rewardsInUSD, userCountryCode]);

  // Step: 'category' | 'payment' | 'confirm' | 'success'
  const [step, setStep] = useState('category');
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Cards state - fetched from API
  const [fetchedCards, setFetchedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);

  // Payment processing state
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [transactionReference, setTransactionReference] = useState(null);

  // Cashback code generation state
  const [generatingCashbackCode, setGeneratingCashbackCode] = useState(false);

  // Exchange rate and points conversion rate state (fetched from API)
  const [exchangeRate, setExchangeRate] = useState(1350); // Default fallback
  const [boldCreditPerNaira, setBoldCreditPerNaira] = useState(0.7407); // Default fallback

  // Fetch fresh user data, cards, and exchange rate when payment step opens
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (step !== 'payment') return;

      setCardsLoading(true);

      try {
        // Fetch user data, cards, and exchange rate in parallel
        const [, cardsResult, defaultsResult] = await Promise.allSettled([
          // Refresh user data (this updates context state including rewards balance)
          fetchUserData(),
          // Fetch saved cards
          bonndleAuthApi.getCards(),
          // Fetch exchange rate from defaults
          defaultsApi.get(),
        ]);

        // Update cards if successful
        if (cardsResult.status === 'fulfilled') {
          const cards = Array.isArray(cardsResult.value) ? cardsResult.value : [];
          setFetchedCards(cards);
          // Auto-select first card if available and no payment selected
          if (cards.length > 0 && !selectedPayment) {
            setSelectedPayment(cards[0]);
          }
        }

        // Update exchange rate and boldCreditPerNaira if successful
        if (defaultsResult.status === 'fulfilled') {
          if (defaultsResult.value?.oneUSDtoNGN) {
            setExchangeRate(defaultsResult.value.oneUSDtoNGN);
            console.log('Fetched exchange rate:', defaultsResult.value.oneUSDtoNGN);
          }
          if (defaultsResult.value?.boldCreditPerNaira) {
            setBoldCreditPerNaira(defaultsResult.value.boldCreditPerNaira);
            console.log('Fetched boldCreditPerNaira:', defaultsResult.value.boldCreditPerNaira);
          }
        }
      } catch (err) {
        console.error('Error fetching payment data:', err);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchPaymentData();
  }, [step, fetchUserData]);

  // Product data states
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  // Category and selection states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBundleType, setSelectedBundleType] = useState('daily'); // 'daily' | 'weekly' | 'monthly' | 'yearly' | 'special'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  // Account lookup states (for bill payment)
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [accountValidated, setAccountValidated] = useState(false);

  // Tab state for Recent / New Transaction switcher
  const [activeTab, setActiveTab] = useState('recent'); // 'recent' | 'new'
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true); // Start as true to show loading by default

  // Fetch all products for this provider
  useEffect(() => {
    const fetchProducts = async () => {
      // Support both snake_case and camelCase field names from different APIs
      const providerUuid = provider?.provider_uuid || provider?.providerUuid;
      if (!providerUuid) {
        console.warn('[PayBillSlider] No provider_uuid found on provider:', provider);
        return;
      }
      setLoadingProducts(true);
      setError(null);
      try {
        // Use production API when useProductionApi flag is set
        const api = useProductionApi ? prodProductsApi : productsApi;
        const products = await api.getByProvider(providerUuid);
        console.log(`[PayBillSlider] Fetched ${products.length} products for ${provider.name} (prod=${useProductionApi})`, products);
        setAllProducts(products);

        // Auto-select first normalized category (prefer Airtime, then Data)
        if (products.length > 0) {
          const normalizedCats = [...new Set(products.map(p => getNormalizedCategory(getProductCategory(p), p.name)))].filter(Boolean);
          console.log('[PayBillSlider] Normalized categories:', normalizedCats);
          // Prefer Airtime, then Data, then first available
          if (normalizedCats.includes('Airtime')) {
            setSelectedCategory('Airtime');
          } else if (normalizedCats.includes('Data')) {
            setSelectedCategory('Data');
            setSelectedBundleType('daily');
          } else if (normalizedCats.length > 0) {
            setSelectedCategory(normalizedCats[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products');
        setAllProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [provider, useProductionApi]);

  // Reset lookup when account number changes
  useEffect(() => {
    setLookupResult(null);
    setLookupError(null);
    setAccountValidated(false);
  }, [accountNumber]);

  // Fetch recent transactions for this provider
  useEffect(() => {
    const fetchRecentTransactions = async () => {
      const providerUuid = provider?.provider_uuid || provider?.providerUuid;
      if (!providerUuid && !provider?.name) return;
      setLoadingRecent(true);
      try {
        const allTransactions = await transactionsApi.getRecentTransactions();
        // Filter transactions by provider name (case-insensitive partial match)
        const providerName = provider.name?.toLowerCase() || '';
        const filtered = allTransactions.filter(tx => {
          const txProvider = tx.providerName?.toLowerCase() || tx.provider?.toLowerCase() || '';
          const txProduct = tx.productName?.toLowerCase() || tx.product?.toLowerCase() || '';
          // Match by provider name or if product name contains provider name
          return txProvider.includes(providerName) ||
                 providerName.includes(txProvider) ||
                 txProduct.includes(providerName);
        });
        setRecentTransactions(filtered.slice(0, 10)); // Limit to 10 recent
      } catch (err) {
        console.error('Failed to fetch recent transactions:', err);
        setRecentTransactions([]);
      } finally {
        setLoadingRecent(false);
      }
    };
    fetchRecentTransactions();
  }, [provider]);

  // Extract unique categories from products (exclude Uncategorized)
  const categories = useMemo(() => {
    const cats = [...new Set(allProducts.map(p => getProductCategory(p)))]
      .filter(Boolean)
      .filter(cat => !cat.toLowerCase().includes('uncategorized'));
    return cats.sort();
  }, [allProducts]);

  // Group products by NORMALIZED category (Airtime, Data, etc.) - Flutter approach
  const normalizedProductGroups = useMemo(() => {
    const groups = {};
    allProducts.forEach(product => {
      const normalizedCat = getNormalizedCategory(getProductCategory(product), product.name);
      if (!groups[normalizedCat]) {
        groups[normalizedCat] = [];
      }
      groups[normalizedCat].push(product);
    });
    console.log('[PayBillSlider] Normalized product groups:', Object.keys(groups), groups);
    return groups;
  }, [allProducts]);

  // Get main category types for the switch (Airtime, Data, etc.)
  const mainCategoryTypes = useMemo(() => {
    const types = Object.keys(normalizedProductGroups);
    // Sort: Airtime first, then Data, then others
    return types.sort((a, b) => {
      if (a === 'Airtime') return -1;
      if (b === 'Airtime') return 1;
      if (a === 'Data') return -1;
      if (b === 'Data') return 1;
      return a.localeCompare(b);
    });
  }, [normalizedProductGroups]);

  // Check if current selection is Data category
  const isDataCategory = useMemo(() => {
    return selectedCategory === 'Data';
  }, [selectedCategory]);

  // Get available bundle types for Data category (derived from products)
  const availableBundleTypes = useMemo(() => {
    if (!isDataCategory) return [];
    const dataProducts = normalizedProductGroups['Data'] || [];
    const bundleTypesSet = new Set();
    dataProducts.forEach(product => {
      const bundleType = getProductBundleType(product);
      bundleTypesSet.add(bundleType);
    });
    // Sort by order: daily, weekly, monthly, yearly, special
    const order = ['daily', 'weekly', 'monthly', 'yearly', 'special'];
    const types = Array.from(bundleTypesSet).sort((a, b) => order.indexOf(a) - order.indexOf(b));
    console.log('[PayBillSlider] Available bundle types for Data:', types);
    return types;
  }, [isDataCategory, normalizedProductGroups]);

  // Products for the selected normalized category
  const categoryProducts = useMemo(() => {
    if (!selectedCategory) return [];
    return normalizedProductGroups[selectedCategory] || [];
  }, [normalizedProductGroups, selectedCategory]);

  // Products filtered by bundle type (for Data category)
  const bundleFilteredProducts = useMemo(() => {
    if (!isDataCategory) return categoryProducts;
    return categoryProducts.filter(product => {
      const productBundleType = getProductBundleType(product);
      return productBundleType === selectedBundleType;
    });
  }, [isDataCategory, categoryProducts, selectedBundleType]);

  // Quick amounts (first 3 products from filtered list)
  const quickAmounts = useMemo(() => {
    const productsToShow = isDataCategory ? bundleFilteredProducts : categoryProducts;
    return productsToShow.slice(0, 3);
  }, [isDataCategory, bundleFilteredProducts, categoryProducts]);

  // Filtered products for search slider - searches ALL products across all bundle types
  const filteredProducts = useMemo(() => {
    if (!productSearch) return allProducts;
    return allProducts.filter(p =>
      p.name.toLowerCase().includes(productSearch.toLowerCase())
    );
  }, [allProducts, productSearch]);

  // Legacy compatibility - grouped categories
  const groupedCategories = useMemo(() => {
    const groups = {};
    categories.forEach(cat => {
      const mainCat = getSimpleCategoryName(cat);
      if (!groups[mainCat]) {
        groups[mainCat] = [];
      }
      groups[mainCat].push(cat);
    });
    return groups;
  }, [categories]);

  // Check if provider is a bill payment provider (has utility-related categories)
  const isBillPaymentProvider = useMemo(() => {
    return categories.some(cat => {
      const name = cat.toLowerCase();
      return name.includes('bill payment') ||
             name.includes('bill_payment') ||
             name.includes('electric') ||
             name.includes('power') ||
             name.includes('cable') ||
             name.includes('tv') ||
             name.includes('internet') ||
             name.includes('water') ||
             name.includes('utilit');
    });
  }, [categories]);

  // Get selected main category type (legacy)
  const selectedMainCategory = useMemo(() => {
    // For normalized categories, just return the selectedCategory
    if (['Airtime', 'Data'].includes(selectedCategory)) {
      return selectedCategory;
    }
    if (!selectedCategory) return null;
    return getSimpleCategoryName(selectedCategory);
  }, [selectedCategory]);

  // Get subcategories for selected main category (legacy - for non-normalized categories)
  const subcategories = useMemo(() => {
    if (!selectedMainCategory || !groupedCategories[selectedMainCategory]) return [];
    const filtered = groupedCategories[selectedMainCategory].filter(cat => cat.includes('>'));
    const bundleOrder = ['Daily', 'Weekly', 'Monthly', 'Yearly', 'Special'];
    return filtered.sort((a, b) => {
      const aType = getShortCategoryName(a);
      const bType = getShortCategoryName(b);
      return bundleOrder.indexOf(aType) - bundleOrder.indexOf(bType);
    });
  }, [selectedMainCategory, groupedCategories]);

  // Check if current category is airtime type
  const isAirtime = useMemo(() => {
    return isAirtimeCategory(selectedCategory);
  }, [selectedCategory]);

  // Check if current category is Bill Payment type (includes all utility categories)
  const isBillPayment = useMemo(() => {
    if (!selectedCategory) return false;
    const name = selectedCategory.toLowerCase();
    return name.includes('bill payment') ||
           name.includes('bill_payment') ||
           name.includes('electric') ||
           name.includes('power') ||
           name.includes('cable') ||
           name.includes('tv') ||
           name.includes('internet') ||
           name.includes('water') ||
           name.includes('utilit');
  }, [selectedCategory]);

  // Check if current category is Gift Card/eVoucher type
  const isGiftCard = useMemo(() => {
    if (!selectedCategory) return false;
    const name = selectedCategory.toLowerCase();
    return name.includes('voucher') || name.includes('gift') || name.includes('e_voucher');
  }, [selectedCategory]);

  // Get price from product
  const getProductPrice = (product) => {
    if (!product) return 0;
    if (product.price !== undefined) return parseFloat(product.price);
    if (product.amount !== undefined) return parseFloat(product.amount);
    if (product.face_value !== undefined) return parseFloat(product.face_value);
    if (product.min_price !== undefined) return parseFloat(product.min_price);
    return 0;
  };

  // Get min/max for airtime
  const airtimeRange = useMemo(() => {
    if (!isAirtime) return { min: 50, max: 50000 };
    // Find product with min/max values
    const airtimeProducts = categoryProducts.filter(p =>
      p.price_type === 'range' || p.min_price > 0 || p.max_price > 0
    );
    if (airtimeProducts.length > 0) {
      const minPrices = airtimeProducts.map(p => p.min_price || 50).filter(v => v > 0);
      const maxPrices = airtimeProducts.map(p => p.max_price || 50000).filter(v => v > 0);
      return {
        min: Math.min(...minPrices, 50),
        max: Math.max(...maxPrices, 50000)
      };
    }
    return { min: 50, max: 50000 };
  }, [isAirtime, categoryProducts]);

  // Quick amounts for airtime (5%, 10%, 20%, 25% of max)
  const airtimeQuickAmounts = useMemo(() => {
    if (!isAirtime) return [];
    const max = airtimeRange.max;
    const percentages = [5, 10, 20, 25];
    return percentages.map(pct => ({
      percent: pct,
      amount: Math.round(max * pct / 100)
    }));
  }, [isAirtime, airtimeRange.max]);

  // Get min/max for bill payments
  const billPaymentRange = useMemo(() => {
    if (!isBillPayment) return { min: 100, max: 500000 };
    // Find product with min/max values
    const billProducts = categoryProducts.filter(p =>
      p.price_type === 'range' || p.min_price > 0 || p.max_price > 0
    );
    if (billProducts.length > 0) {
      const minPrices = billProducts.map(p => p.min_price || 100).filter(v => v > 0);
      const maxPrices = billProducts.map(p => p.max_price || 500000).filter(v => v > 0);
      return {
        min: Math.min(...minPrices, 100),
        max: Math.max(...maxPrices, 500000)
      };
    }
    return { min: 100, max: 500000 };
  }, [isBillPayment, categoryProducts]);

  // Check if gift card is a range product (no fixed options, just min/max)
  const isGiftCardRange = useMemo(() => {
    if (!isGiftCard) return false;
    // Check if there are range products with min/max prices
    const rangeProducts = categoryProducts.filter(p =>
      p.price_type === 'range' || (p.min_price > 0 && p.max_price > 0)
    );
    // It's a range product if we have range products OR no fixed-price products
    const fixedProducts = categoryProducts.filter(p =>
      p.price_type !== 'range' && !p.min_price && p.price > 0
    );
    return rangeProducts.length > 0 || fixedProducts.length === 0;
  }, [isGiftCard, categoryProducts]);

  // Get min/max for gift cards (extract operator prices from metadata for USD amounts)
  const giftCardRange = useMemo(() => {
    if (!isGiftCard) return { min: 10, max: 200, minNGN: 0, maxNGN: 0, exchangeRate: 0 };

    // Find range product with metadata containing operator prices
    const rangeProduct = categoryProducts.find(p =>
      p.price_type === 'range' && p.metadata?.price
    );

    if (rangeProduct?.metadata?.price) {
      const { min: minData, max: maxData } = rangeProduct.metadata.price;
      const exchangeRate = rangeProduct.metadata.exchange_rate || 0;
      return {
        min: parseFloat(minData?.operator) || 10,
        max: parseFloat(maxData?.operator) || 200,
        minNGN: parseFloat(minData?.user) || rangeProduct.min_price || 0,
        maxNGN: parseFloat(maxData?.user) || rangeProduct.max_price || 0,
        exchangeRate: exchangeRate
      };
    }

    // Fallback to min_price/max_price if no metadata
    const giftProducts = categoryProducts.filter(p =>
      p.price_type === 'range' || p.min_price > 0 || p.max_price > 0
    );
    if (giftProducts.length > 0) {
      const minPrices = giftProducts.map(p => p.min_price || 1000).filter(v => v > 0);
      const maxPrices = giftProducts.map(p => p.max_price || 100000).filter(v => v > 0);
      return {
        min: Math.min(...minPrices, 10),
        max: Math.max(...maxPrices, 200),
        minNGN: 0,
        maxNGN: 0,
        exchangeRate: 0
      };
    }
    return { min: 10, max: 200, minNGN: 0, maxNGN: 0, exchangeRate: 0 };
  }, [isGiftCard, categoryProducts]);

  // Get currency for gift cards (from first product or provider)
  const giftCardCurrency = useMemo(() => {
    if (!isGiftCard) return 'USD';
    // Try to get currency from first gift card product
    const firstProduct = categoryProducts.find(p => p.currency);
    return firstProduct?.currency || provider?.currency || 'USD';
  }, [isGiftCard, categoryProducts, provider?.currency]);

  // Get currency symbol for input prefix
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'NGN': '₦',
      'EUR': '€',
      'GBP': '£',
      'KES': 'KSh',
      'GHS': '₵',
      'ZAR': 'R',
    };
    return symbols[currency] || currency + ' ';
  };

  // Quick amounts for gift cards (10%, 20%, 50%, 100% of max) - in USD with NGN equivalent
  const giftCardQuickAmounts = useMemo(() => {
    if (!isGiftCard) return [];
    const max = giftCardRange.max;
    const exchangeRate = giftCardRange.exchangeRate || 0;
    const percentages = [10, 20, 50, 100];
    return percentages.map(pct => {
      const usdAmount = Math.round(max * pct / 100);
      return {
        percent: pct,
        amount: usdAmount,
        ngnAmount: exchangeRate > 0 ? Math.round(usdAmount * exchangeRate) : 0
      };
    });
  }, [isGiftCard, giftCardRange.max, giftCardRange.exchangeRate]);

  // Convert USD to NGN for gift cards
  const convertToNGN = (usdAmount) => {
    if (!giftCardRange.exchangeRate || giftCardRange.exchangeRate === 0) return 0;
    return Math.round(usdAmount * giftCardRange.exchangeRate);
  };

  // Gift card custom amount state
  const [giftCardCustomAmount, setGiftCardCustomAmount] = useState('');

  // Calculate effective amount
  const effectiveAmount = useMemo(() => {
    if (isAirtime && customAmount) {
      return parseFloat(customAmount) || 0;
    }
    if (isBillPayment && customAmount) {
      return parseFloat(customAmount) || 0;
    }
    if (isGiftCard && giftCardCustomAmount) {
      return parseFloat(giftCardCustomAmount) || 0;
    }
    return selectedProduct ? getProductPrice(selectedProduct) : 0;
  }, [isAirtime, isBillPayment, customAmount, isGiftCard, giftCardCustomAmount, selectedProduct]);

  // Calculate cashback
  const cashbackAmount = (effectiveAmount * CASHBACK_PERCENT / 100).toFixed(2);

  // Format currency
  const formatPrice = (price, currency = 'NGN') => {
    const curr = currency || provider?.currency || 'NGN';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleClose = () => {
    // Trigger group chat refresh if closing after successful payment with a group deal
    if (step === 'success' && dealGroupId) {
      triggerGroupChatRefresh();
    }
    setStep('category');
    setSelectedProduct(null);
    setProductSearch('');
    setAccountNumber('');
    setCustomAmount('');
    setLookupResult(null);
    setLookupError(null);
    setAccountValidated(false);
    onClose();
  };

  const handleBack = () => {
    if (step === 'payment') {
      setStep('category');
    } else if (step === 'confirm') {
      setStep('payment');
    }
  };

  const handleMainCategorySelect = (mainCat) => {
    // Use normalized category directly (Airtime, Data, etc.)
    setSelectedCategory(mainCat);
    setSelectedProduct(null);
    setCustomAmount('');
    // Reset bundle type to first available when switching to Data
    if (mainCat === 'Data') {
      setSelectedBundleType('daily');
    }
  };

  const handleBundleTypeSelect = (bundleType) => {
    setSelectedBundleType(bundleType);
    setSelectedProduct(null);
  };

  const handleSubcategorySelect = (subcat) => {
    setSelectedCategory(subcat);
    setSelectedProduct(null);
    setCustomAmount('');
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setShowProductDropdown(false);
    setProductSearch('');
    setCustomAmount('');
  };

  const handleQuickAmountSelect = (product) => {
    const price = getProductPrice(product);
    if (isAirtime && price === 0) {
      // Variable airtime - don't select product, use custom amount
      return;
    }
    setSelectedProduct(product);
    setCustomAmount('');
  };

  // Validate account ID for bill payment
  const handleValidateAccount = async () => {
    if (!accountNumber || !selectedProduct) return;

    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);

    try {
      const productId = selectedProduct.product_uuid || selectedProduct.id;
      // Use production API when useProductionApi flag is set
      const api = useProductionApi ? prodProductsApi : productsApi;
      const result = await api.lookup(productId, accountNumber);
      setLookupResult(result);
      setAccountValidated(true);
    } catch (err) {
      console.error('Account lookup failed:', err);
      setLookupError(err.message || 'Failed to validate account');
      setAccountValidated(false);
    } finally {
      setLookupLoading(false);
    }
  };

  // Check if can proceed
  const canProceed = useMemo(() => {
    if (!accountNumber) return false;

    if (isAirtime) {
      const amt = parseFloat(customAmount) || 0;
      return amt >= airtimeRange.min && amt <= airtimeRange.max;
    }

    if (isBillPayment) {
      const amt = parseFloat(customAmount) || 0;
      return amt >= billPaymentRange.min && amt <= billPaymentRange.max;
    }

    if (isGiftCard && isGiftCardRange) {
      const amt = parseFloat(giftCardCustomAmount) || 0;
      return amt >= giftCardRange.min && amt <= giftCardRange.max;
    }

    return !!selectedProduct;
  }, [accountNumber, isAirtime, isBillPayment, customAmount, airtimeRange, billPaymentRange, isGiftCard, isGiftCardRange, giftCardCustomAmount, giftCardRange, selectedProduct]);

  const handleProceedToPayment = () => {
    setStep('payment');
  };

  // Handle proceeding to confirm step - generate cashback code for non-rewards payments
  const handleProceedToConfirm = async () => {
    // If paying with Bonndle Rewards, skip cashback code generation
    if (selectedPayment?.id === 'rewards') {
      setStep('confirm');
      return;
    }

    // If promo code already exists (manually entered or from initial prop), skip generation
    if (promoCode) {
      setStep('confirm');
      return;
    }

    setGeneratingCashbackCode(true);

    try {
      let groupId = dealGroupId;
      let marketplaceDealId = dealMarketplaceId;

      // If deal info was passed from GroupDealsDrawer, use it directly
      if (groupId && marketplaceDealId) {
        console.log('Cashback Code - Using passed deal info:', { groupId, marketplaceDealId });
      } else {
        // Fallback: Fetch groups and marketplace deals (for direct Shop Tab access)
        console.log('Cashback Code - No deal info passed, fetching from API...');

        // Step 1: Get user's groups
        const groups = await groupsApi.getAll('JOINED');
        console.log('Cashback Code - User groups:', groups);

        if (!groups || groups.length === 0) {
          console.log('Cashback Code - No groups found, proceeding without code');
          setStep('confirm');
          return;
        }

        // Use the first joined group
        const userGroup = groups[0];
        groupId = userGroup.uuid || userGroup.id || userGroup.group_uuid;
        console.log('Cashback Code - Using group:', groupId, userGroup.name);

        // Step 2: Get marketplace deals and find one matching the provider
        const marketplaceDeals = await marketplaceDealsApi.getAll();
        console.log('Cashback Code - Marketplace deals:', marketplaceDeals);

        if (!marketplaceDeals || marketplaceDeals.length === 0) {
          console.log('Cashback Code - No marketplace deals found, proceeding without code');
          setStep('confirm');
          return;
        }

        // Try to find a deal matching the provider name
        const providerName = provider?.name?.toLowerCase() || '';
        let matchedDeal = marketplaceDeals.find(deal => {
          const dealMerchant = deal.merchant_name?.toLowerCase() || deal.marketplace?.toLowerCase() || deal.name?.toLowerCase() || '';
          return dealMerchant.includes(providerName) || providerName.includes(dealMerchant);
        });

        // If no exact match, use the first deal (generic Bonndle deal)
        if (!matchedDeal) {
          matchedDeal = marketplaceDeals[0];
          console.log('Cashback Code - No provider match, using first deal:', matchedDeal);
        } else {
          console.log('Cashback Code - Matched deal:', matchedDeal);
        }

        marketplaceDealId = matchedDeal.uuid || matchedDeal.id || matchedDeal.marketplace_deal_uuid;
      }

      // Generate the cashback code
      const codeResponse = await groupCodeApi.generateCode(groupId, marketplaceDealId);
      console.log('Cashback Code - Generated:', codeResponse);

      // Extract the code from response (API may return { code: 'ABC123' } or { data: { code: 'ABC123' } })
      const generatedCode = codeResponse?.code || codeResponse?.data?.code || codeResponse?.discount_code || codeResponse?.discountCode ||
        codeResponse?.voucher_code || codeResponse?.voucherCode ||
        codeResponse?.groupCode?.code || codeResponse?.group_code?.code ||
        (typeof codeResponse?.groupCode === 'string' ? codeResponse.groupCode : null) ||
        (typeof codeResponse?.group_code === 'string' ? codeResponse.group_code : null) ||
        (typeof codeResponse === 'string' ? codeResponse : null);

      if (generatedCode) {
        setPromoCode(generatedCode);
        setHasAutoAppliedCode(true);
        console.log('Cashback Code - Applied:', generatedCode);
      }
    } catch (error) {
      console.error('Cashback Code - Generation failed:', error);
      // Don't block the payment flow if code generation fails
    } finally {
      setGeneratingCashbackCode(false);
      setStep('confirm');
    }
  };

  // Generate a unique payment reference (e.g., web_ttvs5e9je9j)
  const generatePaymentReference = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `web_${timestamp}${randomStr}`;
  };

  const handleConfirmPayment = async () => {
    setProcessingPayment(true);
    setPaymentError(null);

    // Capture currency for use in background async function
    const productCurrency = selectedProduct?.currency || (isGiftCard ? giftCardCurrency : provider?.currency) || 'NGN';

    try {
      // Generate a unique payment reference
      const paymentRef = generatePaymentReference();

      // Get product ID - for custom amounts (airtime/bills), find the variable product
      let productId = selectedProduct?.product_uuid || selectedProduct?.uuid || selectedProduct?.id;

      // If no product selected (custom amount), find the appropriate variable product
      if (!productId && categoryProducts.length > 0) {
        // Find a range-type product (variable amount product) in the current category
        const variableProduct = categoryProducts.find(p =>
          p.price_type === 'range' || p.min_price > 0 || p.max_price > 0
        );
        if (variableProduct) {
          productId = variableProduct.product_uuid || variableProduct.uuid || variableProduct.id;
        } else {
          // Fallback to first product in category
          const firstProduct = categoryProducts[0];
          productId = firstProduct?.product_uuid || firstProduct?.uuid || firstProduct?.id;
        }
      }

      console.log('Payment - productId resolved:', productId, 'selectedProduct:', selectedProduct?.name || 'custom amount');

      // Get currency
      const currency = selectedProduct?.currency || provider?.currency || 'NGN';

      // Fetch exchange rate from /defaults endpoint and calculate USD amount
      let totalAmountInUSD = effectiveAmount; // Default for USD
      if (currency !== 'USD') {
        try {
          const defaults = await defaultsApi.get();
          const exchangeRate = defaults.oneUSDtoNGN || 1350; // Fallback rate
          totalAmountInUSD = effectiveAmount / exchangeRate;
          console.log('Payment - Exchange rate:', exchangeRate, 'totalAmountInUSD:', totalAmountInUSD);
        } catch (err) {
          console.warn('Failed to fetch exchange rate, using fallback:', err);
          totalAmountInUSD = effectiveAmount / 1350; // Fallback calculation
        }
      }

      // Format account number - ensure Nigerian numbers have country code with + prefix
      let formattedAccount = accountNumber.replace(/^\+/, ''); // Remove leading + if present
      // If Nigerian currency and number starts with 0, prepend 234
      if (currency === 'NGN' && formattedAccount.startsWith('0')) {
        formattedAccount = '234' + formattedAccount.substring(1);
      }
      // Add + prefix to account number (required by API)
      formattedAccount = '+' + formattedAccount;

      // Ensure USD amount has proper precision (4 decimal places required by API)
      const formattedUSDAmount = parseFloat(totalAmountInUSD.toFixed(4));

      // Build purchase data with correct field names (API uses camelCase)
      const purchaseData = {
        account: formattedAccount,
        productId: productId,
        purchaseAmount: Math.round(effectiveAmount),
        donationAmount: 0,
        totalAmount: Math.round(effectiveAmount),
        totalAmountInUSD: formattedUSDAmount,
        quantity: 1,
        currencyCode: currency,
      };

      let response;

      if (selectedPayment?.id === 'rewards') {
        // Purchase with Bonndle Rewards (points)
        // Get boldCreditPerNaira from defaults to calculate points correctly
        // Based on successful transaction analysis: points = amount * boldCreditPerNaira
        const defaults = await defaultsApi.get();
        const boldCreditPerNaira = defaults.boldCreditPerNaira || 0.7407;
        const currentExchangeRate = defaults.oneUSDtoNGN || 1350;

        // Calculate points needed using boldCreditPerNaira (matches backend calculation)
        // For NGN: points = amount * boldCreditPerNaira
        // For USD: points = amount * exchangeRate * boldCreditPerNaira
        const amountInNGN = currency === 'USD' ? effectiveAmount * currentExchangeRate : effectiveAmount;
        const pointsNeeded = parseFloat((amountInNGN * boldCreditPerNaira).toFixed(2));

        // For points purchases, calculate totalAmountInUSD with 4 decimal places (required by API)
        const pointsAmountInUSD = parseFloat((amountInNGN / currentExchangeRate).toFixed(4));
        const pointsPurchaseData = {
          account: purchaseData.account,
          productId: purchaseData.productId,
          purchaseAmount: Math.round(amountInNGN),
          donationAmount: 0,
          totalAmount: Math.round(amountInNGN),
          totalAmountInUSD: pointsAmountInUSD, // USD amount with 4dp precision required by API
          quantity: 1,
          currencyCode: 'NGN', // Points purchases always use NGN amount
        };

        console.log('Purchasing with points:', {
          ...pointsPurchaseData,
          calculatedPoints: pointsNeeded,
          boldCreditPerNaira,
          exchangeRate: currentExchangeRate,
          originalCurrency: currency,
          originalAmount: effectiveAmount,
        });
        response = await productsApi.purchaseWithPoints(pointsPurchaseData);
        console.log('Purchase with points successful:', response);
      } else if (selectedPayment?.id === 'test-card') {
        // TEST MODE: Simulate payment without calling actual API
        console.log('TEST MODE: Simulating card payment (no actual charge)');
        response = {
          success: true,
          data: {
            reference: `TEST-${Date.now()}`,
            status: 'success',
          },
          message: 'Test payment simulated successfully',
        };
        console.log('TEST MODE: Simulated payment response:', response);

      } else if (selectedPayment?.id === 'bank_transfer') {
        // Purchase with Bank Transfer - opens payment processor page
        const transferReference = `TT${Date.now().toString(36)}`;
        const transferPurchaseData = {
          reference: transferReference,
          account: purchaseData.account,
          productId: purchaseData.productId,
          purchaseAmount: purchaseData.purchaseAmount,
          donationAmount: 0,
          totalAmount: purchaseData.totalAmount,
          quantity: 1,
          currencyCode: purchaseData.currencyCode,
          // Include discount code if promo code is present
          ...(promoCode ? { discountCode: promoCode } : {}),
        };

        console.log('Purchasing with bank transfer:', transferPurchaseData);
        response = await productsApi.purchaseWithTransfer(transferPurchaseData);
        console.log('Bank transfer initiated:', response);

        // Check payment status from response - API returns { transaction, payment } objects
        const paymentStatus = response?.payment?.payment_status ||
          response?.payment_status ||
          response?.transaction?.transaction_status;
        console.log('Bank transfer payment_status:', paymentStatus);

        // Get payment URL from response - check nested payment object first
        const paymentUrl = response?.payment?.authorizationUrl ||
          response?.payment?.authorization_url ||
          response?.payment?.paymentUrl ||
          response?.payment?.payment_url ||
          response?.data?.paymentUrl ||
          response?.data?.payment_url ||
          response?.paymentUrl ||
          response?.payment_url ||
          response?.data?.authorizationUrl ||
          response?.authorizationUrl;
        console.log('Bank transfer paymentUrl:', paymentUrl);

        if (paymentUrl) {
          // Open payment page in new window
          const paymentWindow = window.open(paymentUrl, '_blank', 'noopener,noreferrer');

          // Show a message to the user
          if (paymentWindow) {
            // Store reference for success handling - check payment object first
            setTransactionReference(
              response?.payment?.reference ||
              response?.transaction?.reference ||
              response?.data?.reference ||
              response?.reference ||
              transferReference
            );
            // Move to success step - user will complete payment in new tab
            setStep('success');
            setProcessingPayment(false);
            return;
          } else {
            // Popup was blocked, show manual link
            throw new Error('Please allow popups to complete payment, or click the payment link.');
          }
        } else {
          throw new Error('No payment URL received from server');
        }

      } else {
        // Purchase with card
        const cardPurchaseData = {
          ...purchaseData,
          cardId: selectedPayment?.uuid || selectedPayment?.id,
          // Include discount code if promo code is present
          ...(promoCode ? { discountCode: promoCode } : {}),
        };
        console.log('Purchasing with card:', cardPurchaseData);
        response = await productsApi.purchaseWithCard(cardPurchaseData);
        console.log('Purchase with card successful:', response);

      }

      // Store the actual transaction reference from API response
      const actualReference = response?.data?.reference || response?.reference || paymentRef;
      setTransactionReference(actualReference);

      // Payment successful - move to success step
      setStep('success');

      // Post cashback announcement in background (fire and forget) after transaction success
      if (dealGroupId && promoCode && parseFloat(cashbackAmount) > 0) {
        const isTestPayment = selectedPayment?.id === 'test-card';
        const userTag = profileForm?.bonndleTag || user?.bold_tag || user?.bonndleTag || user?.boldtag || user?.boldTag;

        if (userTag) {
          // Background post - don't await, fire and forget
          (async () => {
            try {
              const merchantName = displayName || provider?.name || 'merchant';
              const savedAmount = formatPrice(parseFloat(cashbackAmount), productCurrency);
              const announcementText = `@${userTag} just saved ${savedAmount} on a ${merchantName} purchase`;
              // Append [test] marker for test payments only
              const backendText = isTestPayment ? `${announcementText} [test]` : announcementText;

              // Gender-based avatar using local memojis
              const userGender = (profileForm?.gender || user?.gender || '').toLowerCase();
              const femaleMemojis = ['/avatars/Female-memoji-1.svg', '/avatars/Female-memoji-2.svg', '/avatars/Female-memoji-3.svg', '/avatars/Female-memoji-4.svg'];
              const maleMemojis = ['/avatars/Male-memoji-5.svg', '/avatars/Male-memoji-6.svg', '/avatars/Male-memoji-7.svg'];
              const avatarList = userGender === 'female' ? femaleMemojis : maleMemojis;
              const userAvatar = avatarList[Math.floor(Math.random() * avatarList.length)];

              // Use dealGroupId directly as feedItemId
              const commentResult = await wixCommentsApi.addComment(dealGroupId, {
                author: 'bonndleagent',
                avatar: userAvatar,
                text: backendText,
                userId: user?.uuid || user?.id || null,
              });
              console.log('Cashback announcement posted:', commentResult);

              // Cache test comment for cleanup on sign out
              if (isTestPayment) {
                const testComments = JSON.parse(localStorage.getItem('bonndleTestComments') || '[]');
                testComments.push({
                  commentId: commentResult.comment?.id,
                  feedItemId: dealGroupId,
                  timestamp: Date.now(),
                });
                localStorage.setItem('bonndleTestComments', JSON.stringify(testComments));
              }
            } catch (err) {
              console.error('Background: Failed to post cashback announcement:', err);
            }
          })();
        }
      }
    } catch (error) {
      console.error('Payment failed:', error);
      setPaymentError(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (!provider) return null;

  // Step: Category Selection with Products
  if (step === 'category') {
    const productCurrency = selectedProduct?.currency || (isGiftCard ? giftCardCurrency : provider?.currency) || 'NGN';

    return (
      <>
        {!hideBackdrop && <div className="backdrop animate-fade-in" onClick={handleClose} />}
        <div className="slide-panel">
          <div>
            {/* Header - Fixed */}
            <div className="slide-panel-header flex items-center gap-3 p-4 border-b border-gray-100">
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <ArrowLeft size={20} />
              </button>
              {logoUrl ? (
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 shadow flex items-center justify-center p-1">
                  <img
                    src={logoUrl}
                    alt={displayName}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.parentElement.style.display = 'none';
                      e.target.parentElement.nextSibling.style.display = 'flex';
                    }}
                  />
                </div>
              ) : null}
              <div
                className={`w-10 h-10 bg-indigo-600 rounded-xl items-center justify-center text-white font-bold text-sm shadow ${logoUrl ? 'hidden' : 'flex'}`}
              >
                {displayName.charAt(0) || 'P'}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">{displayName}</h3>
                <p className="text-emerald-600 font-bold text-[10px] uppercase tracking-tight">{CASHBACK_PERCENT}% Cashback</p>
              </div>
            </div>

            {/* Tab Banner - Recent | New Transaction */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('recent')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-all ${
                  activeTab === 'recent'
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Clock size={16} />
                Recent
              </button>
              <button
                onClick={() => setActiveTab('new')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-all ${
                  activeTab === 'new'
                    ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Plus size={16} />
                New Transaction
              </button>
            </div>

            <div className="slide-panel-content p-4">
              {/* Recent Tab Content */}
              {activeTab === 'recent' && (
                <div>
                  {loadingRecent ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-indigo-600" />
                      <span className="ml-2 text-gray-500">Loading recent transactions...</span>
                    </div>
                  ) : recentTransactions.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Clock size={32} className="text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm mb-2">No recent transactions</p>
                      <p className="text-gray-400 text-xs">Your recent purchases from {displayName} will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
                        Recent Purchases from {displayName}
                      </p>
                      {recentTransactions.map((tx, index) => {
                        // Extract data using same fields as TransactionReceiptPage
                        const txAmount = tx.amount || 0;
                        const txDate = tx.created_at;
                        const rawProduct = tx.metadata?.product || tx.description || 'Purchase';
                        const txRecipient = tx.metadata?.recipient || '';
                        const txCategory = tx.category || tx.metadata?.category || '';
                        // Get product_uuid directly from transaction (cached from original purchase)
                        const txProductUuid = tx.product_uuid || tx.metadata?.product_uuid;

                        // Transform product name for airtime (show "₦{amount} Airtime" instead of "10-50000 NGN PIN")
                        const isAirtime = txCategory.toLowerCase().includes('pin') ||
                                          txCategory.toLowerCase().includes('top up') ||
                                          txCategory.toLowerCase().includes('airtime');
                        const txProduct = isAirtime ? `₦${txAmount.toLocaleString()} Airtime` : rawProduct;

                        return (
                          <button
                            key={tx.transaction_uuid || tx.id || index}
                            onClick={() => {
                              // Pre-fill form with this transaction's details
                              if (txRecipient) setAccountNumber(txRecipient);
                              if (txAmount) setCustomAmount(String(txAmount));

                              // Set the category based on transaction category
                              const matchingCategory = categories.find(cat =>
                                cat.toLowerCase().includes(txCategory.toLowerCase()) ||
                                txCategory.toLowerCase().includes(cat.toLowerCase())
                              );

                              if (matchingCategory) {
                                setSelectedCategory(matchingCategory);
                              } else if (isAirtime) {
                                const airtimeCategory = categories.find(cat =>
                                  cat.toLowerCase().includes('pin') ||
                                  cat.toLowerCase().includes('top up')
                                );
                                if (airtimeCategory) {
                                  setSelectedCategory(airtimeCategory);
                                }
                              }

                              // Create product with cached product_uuid from transaction
                              setSelectedProduct({
                                product_uuid: txProductUuid,
                                name: txProduct,
                                price: txAmount,
                                currency: 'NGN',
                                price_type: 'range',
                              });

                              // Go directly to payment step
                              setStep('payment');
                            }}
                            className="w-full p-4 bg-white border border-gray-100 rounded-xl hover:border-indigo-200 hover:bg-indigo-50/30 transition-all text-left group"
                          >
                            {/* Top row: Product Name + Amount */}
                            <div className="flex items-center justify-between gap-3 mb-2">
                              <p className="font-bold text-sm text-gray-900 truncate flex-1">{txProduct}</p>
                              <span className="font-bold text-sm text-indigo-600 shrink-0">
                                {formatPrice(txAmount, 'NGN')}
                              </span>
                            </div>
                            {/* Bottom row: Recipient + Date */}
                            <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                              <span className="truncate flex-1">{txRecipient || 'N/A'}</span>
                              {txDate && (
                                <span className="shrink-0">{new Date(txDate).toLocaleDateString()}</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* New Transaction Tab Content */}
              {activeTab === 'new' && (
                <>
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-indigo-600" />
                  <span className="ml-2 text-gray-500">Loading products...</span>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-500 text-sm">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-indigo-600 text-sm font-bold"
                  >
                    Retry
                  </button>
                </div>
              ) : mainCategoryTypes.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No products available for this provider</p>
                </div>
              ) : (
                <>
                  {/* Main Category Switch (Airtime, Data, etc.) */}
                  <div className="mb-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Select Service</p>
                    <div className="flex flex-wrap gap-2">
                      {mainCategoryTypes.map((mainCat) => (
                        <button
                          key={mainCat}
                          onClick={() => handleMainCategorySelect(mainCat)}
                          className={`px-4 py-2 rounded-full font-bold text-sm transition-all ${
                            selectedCategory === mainCat
                              ? 'bg-indigo-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {mainCat}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Bundle Type Section - Only shows for Data category, hidden for Airtime */}
                  {!isAirtime && (
                  <div className="mb-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
                      Bundle Type
                    </p>
                    {isDataCategory && availableBundleTypes.length > 0 ? (
                      // For Data - show bundle type buttons (daily, weekly, monthly, yearly, special)
                      <div className="flex flex-wrap gap-2">
                        {availableBundleTypes.map((bundleType) => (
                          <button
                            key={bundleType}
                            onClick={() => handleBundleTypeSelect(bundleType)}
                            className={`px-3 py-1.5 rounded-full font-bold text-xs transition-all capitalize ${
                              selectedBundleType === bundleType
                                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                                : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                            }`}
                          >
                            {bundleType}
                          </button>
                        ))}
                      </div>
                    ) : subcategories.length > 0 ? (
                      // Legacy: For Data with category_name subcategories
                      <div className="flex flex-wrap gap-2">
                        {subcategories.map((subcat) => (
                          <button
                            key={subcat}
                            onClick={() => handleSubcategorySelect(subcat)}
                            className={`px-3 py-1.5 rounded-full font-bold text-xs transition-all ${
                              selectedCategory === subcat
                                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                                : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                            }`}
                          >
                            {getShortCategoryName(subcat)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      // Fallback - show selected category
                      <div className="px-3 py-1.5 rounded-full font-bold text-xs bg-indigo-100 text-indigo-700 border-2 border-indigo-300 inline-block">
                        {selectedCategory === 'Data' ? 'Data' : getShortCategoryName(selectedCategory)}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Phone/Account Number Input */}
                  <div className="mb-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      {getAccountLabel(selectedCategory)}
                    </p>
                    <input
                      type={isGiftCard ? 'email' : isAirtime ? 'tel' : 'text'}
                      placeholder={getAccountPlaceholder(selectedCategory)}
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      className="w-full bg-gray-100 px-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                  </div>

                  {/* Product Selection - Search button + Product list (for Data and other non-airtime categories) */}
                  {!isAirtime && !isBillPayment && !(isGiftCard && isGiftCardRange) && (
                    <div className="mb-5">
                      {/* Search All Products Button */}
                      <button
                        onClick={() => setShowProductDropdown(true)}
                        className="w-full bg-gray-100 px-4 py-3 rounded-xl text-sm font-bold outline-none text-left flex items-center justify-between hover:bg-gray-200 transition-all mb-3"
                      >
                        <span className="text-gray-400">Search all products...</span>
                        <Search size={18} className="text-gray-400" />
                      </button>

                      {/* Select Plan Label */}
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Select Plan</p>

                      {/* Products List - Show filtered products for Data, all for others */}
                      {(isDataCategory ? bundleFilteredProducts : categoryProducts).length === 0 ? (
                        <div className="text-center py-6 bg-gray-50 rounded-xl">
                          <p className="text-gray-500 text-sm">No plans available for this bundle type</p>
                        </div>
                      ) : (
                        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden max-h-64 overflow-y-auto">
                          {(isDataCategory ? bundleFilteredProducts : categoryProducts).map((product) => {
                            const productId = product.product_uuid || product.id;
                            const price = getProductPrice(product);
                            const isSelected = (selectedProduct?.product_uuid || selectedProduct?.id) === productId;

                            return (
                              <button
                                key={productId}
                                onClick={() => handleQuickAmountSelect(product)}
                                className={`w-full px-4 py-3 text-left flex items-center justify-between gap-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                                  isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : 'hover:bg-gray-50'
                              }`}
                            >
                              {/* Product name and validity */}
                              <div className="flex-1 min-w-0">
                                <p className={`font-bold text-sm truncate ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                                  {product.name}
                                </p>
                                {product.validity && (
                                  <p className="text-xs text-gray-500 truncate">{product.validity}</p>
                                )}
                              </div>
                              {/* Price */}
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="font-bold text-sm text-indigo-600">
                                  {formatPrice(price, product.currency)}
                                </span>
                                {isSelected && <Check size={16} className="text-indigo-600" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  )}

                  {/* Quick Amounts */}
                  {isAirtime ? (
                    /* Airtime: Show percentage-based quick amounts (5%, 10%, 20%, 25% of max) */
                    <div className="mb-5">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
                        Quick Amounts
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {airtimeQuickAmounts.map(({ percent, amount }) => {
                          const isSelected = customAmount === String(amount);
                          return (
                            <button
                              key={percent}
                              onClick={() => {
                                setCustomAmount(String(amount));
                                setSelectedProduct(null);
                              }}
                              className={`py-3 px-2 rounded-xl font-bold text-xs transition-all text-center ${
                                isSelected
                                  ? 'bg-indigo-600 text-white shadow-lg'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {formatPrice(amount)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {/* Custom Amount Input (for Airtime) */}
                  {isAirtime && (
                    <div className="mb-5">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Other Amount</p>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₦</span>
                        <input
                          type="number"
                          placeholder="Enter amount"
                          value={customAmount}
                          onChange={(e) => {
                            setCustomAmount(e.target.value);
                            setSelectedProduct(null);
                          }}
                          min={airtimeRange.min}
                          max={airtimeRange.max}
                          className="w-full bg-gray-100 pl-8 pr-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Min: {formatPrice(airtimeRange.min)} | Max: {formatPrice(airtimeRange.max)}
                      </p>
                    </div>
                  )}

                  {/* Gift Card Quick Amounts (for range products) */}
                  {isGiftCard && isGiftCardRange && (
                    <>
                      <div className="mb-5">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
                          Quick Amounts
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          {giftCardQuickAmounts.map(({ percent, amount, ngnAmount }) => {
                            const isSelected = giftCardCustomAmount === String(amount);
                            return (
                              <button
                                key={percent}
                                onClick={() => {
                                  setGiftCardCustomAmount(String(amount));
                                  setSelectedProduct(null);
                                }}
                                className={`py-2 px-2 rounded-xl font-bold transition-all text-center flex flex-col items-center ${
                                  isSelected
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                <span className="text-xs">{formatPrice(amount, giftCardCurrency)}</span>
                                {ngnAmount > 0 && (
                                  <span className={`text-[9px] mt-0.5 ${isSelected ? 'text-indigo-200' : 'text-gray-400'}`}>
                                    ₦{ngnAmount.toLocaleString()}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Custom Amount Input (for Gift Cards) */}
                      <div className="mb-5">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Other Amount ({giftCardCurrency})</p>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">{getCurrencySymbol(giftCardCurrency)}</span>
                          <input
                            type="number"
                            placeholder="Enter amount"
                            value={giftCardCustomAmount}
                            onChange={(e) => {
                              setGiftCardCustomAmount(e.target.value);
                              setSelectedProduct(null);
                            }}
                            min={giftCardRange.min}
                            max={giftCardRange.max}
                            className="w-full bg-gray-100 pl-8 pr-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                          />
                        </div>
                        {/* NGN Equivalent */}
                        {giftCardCustomAmount && giftCardRange.exchangeRate > 0 && (
                          <p className="text-sm font-bold text-indigo-600 mt-2">
                            ₦{convertToNGN(parseFloat(giftCardCustomAmount) || 0).toLocaleString()} NGN
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          {formatPrice(giftCardRange.min, giftCardCurrency)} - {formatPrice(giftCardRange.max, giftCardCurrency)}
                          {giftCardRange.minNGN > 0 && giftCardRange.maxNGN > 0 && (
                            <span className="text-gray-400"> (₦{giftCardRange.minNGN.toLocaleString()} - ₦{giftCardRange.maxNGN.toLocaleString()})</span>
                          )}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Bill Payment Amount Input */}
                  {!isAirtime && isBillPayment && (
                    <div className="mb-5">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Enter Amount</p>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₦</span>
                        <input
                          type="number"
                          placeholder="Enter bill amount"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          min={billPaymentRange.min}
                          max={billPaymentRange.max}
                          className="w-full bg-gray-100 pl-8 pr-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Min: ₦{billPaymentRange.min.toLocaleString()} • Max: ₦{billPaymentRange.max.toLocaleString()}
                      </p>
                    </div>
                  )}

                  {/* Product Search Slider */}
                  {showProductDropdown && (
                    <>
                      <div className="backdrop animate-fade-in" onClick={() => {
                        setShowProductDropdown(false);
                        setProductSearch('');
                      }} />
                      <div className="slide-panel">
                        <div className="flex flex-col h-full">
                          {/* Header */}
                          <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setShowProductDropdown(false);
                                  setProductSearch('');
                                }}
                                className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                              >
                                <ArrowLeft size={20} />
                              </button>
                              <h2 className="text-lg font-bold text-gray-900">Search Products</h2>
                            </div>
                            <button
                              onClick={() => {
                                setShowProductDropdown(false);
                                setProductSearch('');
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                            >
                              <X size={20} />
                            </button>
                          </div>

                          {/* Search Input */}
                          <div className="p-4 border-b border-gray-100">
                            <div className="relative">
                              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search data products..."
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                className="w-full bg-gray-100 pl-11 pr-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          </div>

                          {/* Product List */}
                          <div className="slide-panel-content">
                            {filteredProducts.length === 0 ? (
                              <p className="text-center text-gray-400 py-8 text-sm">No products found</p>
                            ) : (
                              <div className="divide-y divide-gray-100">
                                {filteredProducts.map((product) => {
                                  const productId = product.product_uuid || product.id;
                                  const price = getProductPrice(product);
                                  const isSelected = (selectedProduct?.product_uuid || selectedProduct?.id) === productId;

                                  return (
                                    <button
                                      key={productId}
                                      onClick={() => {
                                        handleProductSelect(product);
                                        setShowProductDropdown(false);
                                        setProductSearch('');
                                      }}
                                      className={`w-full p-4 text-left flex items-center justify-between transition-colors ${
                                        isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                                      }`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm text-gray-900">{product.name}</p>
                                      </div>
                                      <div className="flex items-center gap-2 ml-3">
                                        <span className="font-bold text-sm text-indigo-600">{formatPrice(price, product.currency)}</span>
                                        {isSelected && <Check size={18} className="text-indigo-600" />}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Summary */}
                  {effectiveAmount > 0 && (
                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-gray-700">Amount</span>
                        <span className="text-sm font-bold text-gray-900">{formatPrice(effectiveAmount, productCurrency)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-700">Cashback Savings</span>
                        <span className="text-sm font-bold text-emerald-600">+{formatPrice(parseFloat(cashbackAmount), productCurrency)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 slide-panel-footer">
              <button
                onClick={handleProceedToPayment}
                disabled={!canProceed || activeTab === 'recent'}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Proceed <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Payment Method Selection
  if (step === 'payment') {
    const productCurrency = selectedProduct?.currency || (isGiftCard ? giftCardCurrency : provider?.currency) || 'NGN';

    return (
      <>
        {!hideBackdrop && <div className="backdrop animate-fade-in" onClick={handleClose} />}
        <div className="slide-panel">
          <div>
            {/* Header - Fixed */}
            <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <button onClick={handleBack} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                  <ChevronLeft size={20} />
                </button>
                <h2 className="text-lg font-bold">Payment Method</h2>
              </div>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="slide-panel-content p-4">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 shadow flex items-center justify-center p-1">
                      <img
                        src={logoUrl}
                        alt={displayName}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.parentElement.style.display = 'none';
                          e.target.parentElement.nextSibling.style.display = 'flex';
                        }}
                      />
                    </div>
                  ) : null}
                  <div
                    className={`w-10 h-10 bg-indigo-600 rounded-xl items-center justify-center text-white font-bold text-sm shadow ${logoUrl ? 'hidden' : 'flex'}`}
                  >
                    {displayName.charAt(0) || 'P'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-gray-900">{displayName}</h3>
                    <p className="text-xs text-gray-500">{getSimpleCategoryName(selectedCategory) === 'Mobile Top Up' ? 'Airtime' : getSimpleCategoryName(selectedCategory)}</p>
                  </div>
                  <span className="text-lg font-bold text-indigo-600">{formatPrice(effectiveAmount, productCurrency)}</span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Payment Method</p>

                {/* Bonndle Rewards Payment Option - Always show when enabled */}
                {payWithRewardsEnabled && (
                  <button
                    onClick={() => {
                      if (rewardsBalance > 0) {
                        setSelectedPayment({ id: 'rewards', type: 'rewards', name: 'Bonndle Rewards' });
                        setPromoCode(''); // Clear promo code - cashback not applicable for rewards payment
                      }
                    }}
                    disabled={rewardsBalance <= 0}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selectedPayment?.id === 'rewards'
                        ? 'border-indigo-600 bg-indigo-50'
                        : rewardsBalance <= 0
                        ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                        : 'border-gray-100 bg-white hover:border-indigo-200'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                      <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="w-10 h-10" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-gray-900' : 'text-gray-500'}`}>Bonndle Rewards</p>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                          {Math.floor(rewardsBalance).toLocaleString()} Pts
                        </span>
                        <span className="text-gray-400">|</span>
                        <span className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                          ${rewardsInUSD.toFixed(2)}
                        </span>
                        {rewardsInLocalCurrency && userCountryCode !== 'US' && (
                          <>
                            <span className="text-gray-400">|</span>
                            <span className="text-sm text-gray-600 font-medium">
                              {rewardsInLocalCurrency.formatted}
                            </span>
                          </>
                        )}
                        {rewardsBalance <= 0 && (
                          <span className="text-xs text-gray-400 ml-1">• No balance</span>
                        )}
                      </div>
                    </div>
                    {selectedPayment?.id === 'rewards' && <Check size={18} className="text-indigo-600" />}
                  </button>
                )}

                {/* Loading Cards */}
                {cardsLoading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-indigo-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading cards...</span>
                  </div>
                )}

                {/* Saved Cards */}
                {!cardsLoading && fetchedCards.map((card) => {
                  // API returns: cardProvider (VISA), last4Digits, expMonth, expYear, uuid
                  const cardBrand = card.cardProvider || card.brand || 'Card';
                  const cardLast4 = card.last4Digits || card.last4 || '****';
                  const cardExpiry = card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : '';
                  const cardId = card.uuid || card.id;

                  return (
                    <button
                      key={cardId}
                      onClick={() => setSelectedPayment(card)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                        (selectedPayment?.uuid || selectedPayment?.id) === cardId ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white'
                      }`}
                    >
                      <div className="w-10 h-7 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                        <CreditCard size={14} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 capitalize text-sm">{cardBrand} **** {cardLast4}</p>
                        {cardExpiry && <p className="text-xs text-gray-500">Expires {cardExpiry}</p>}
                      </div>
                      {(selectedPayment?.uuid || selectedPayment?.id) === cardId && <Check size={18} className="text-indigo-600" />}
                    </button>
                  );
                })}

                {/* Bank Transfer Payment Option */}
                {!cardsLoading && (
                  <button
                    onClick={() => setSelectedPayment({ id: 'bank_transfer', type: 'bank_transfer', name: 'Bank Transfer' })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selectedPayment?.id === 'bank_transfer' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white hover:border-indigo-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      selectedPayment?.id === 'bank_transfer' ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                      <Landmark size={20} className={selectedPayment?.id === 'bank_transfer' ? 'text-indigo-600' : 'text-gray-600'} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-sm">Pay with Bank Transfer</p>
                      <p className="text-xs text-gray-500">Transfer from any Nigerian bank</p>
                    </div>
                    {selectedPayment?.id === 'bank_transfer' && <Check size={18} className="text-indigo-600" />}
                  </button>
                )}

                {/* Test Card - Show when no saved cards for testing code generation */}
                {!cardsLoading && fetchedCards.length === 0 && (
                  <button
                    onClick={() => setSelectedPayment({ id: 'test-card', type: 'card', last4: '4242', cardProvider: 'TEST' })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      selectedPayment?.id === 'test-card' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="w-10 h-7 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <CreditCard size={14} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-sm">TEST **** 4242</p>
                      <p className="text-xs text-orange-500">Test card for development</p>
                    </div>
                    {selectedPayment?.id === 'test-card' && <Check size={18} className="text-indigo-600" />}
                  </button>
                )}

                {/* Add New Card Button - Show when no cards saved */}
                {!cardsLoading && fetchedCards.length === 0 && (
                  <button
                    onClick={() => {/* TODO: Navigate to add card screen */}}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-gray-200 text-left transition-all flex items-center gap-3 hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                      <Plus size={20} className="text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm text-gray-900">Add New Card</p>
                      <p className="text-xs text-gray-500">Add a debit or credit card</p>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 slide-panel-footer">
              <button
                onClick={handleProceedToConfirm}
                disabled={!selectedPayment || generatingCashbackCode}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingCashbackCode ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Preparing...
                  </>
                ) : (
                  <>
                    Review Payment <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step: Confirmation
  if (step === 'confirm') {
    const productCurrency = selectedProduct?.currency || (isGiftCard ? giftCardCurrency : provider?.currency) || 'NGN';

    return (
      <>
        {!hideBackdrop && <div className="backdrop animate-fade-in" onClick={handleClose} />}
        <div className="slide-panel">
          <div>
            {/* Header - Fixed */}
            <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <button onClick={handleBack} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                  <ChevronLeft size={20} />
                </button>
                <h2 className="text-lg font-bold">Confirm Payment</h2>
              </div>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <div className="slide-panel-content p-4">
              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                  {logoUrl ? (
                    <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 shadow flex items-center justify-center p-1">
                      <img
                        src={logoUrl}
                        alt={displayName}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.parentElement.style.display = 'none';
                          e.target.parentElement.nextSibling.style.display = 'flex';
                        }}
                      />
                    </div>
                  ) : null}
                  <div
                    className={`w-12 h-12 bg-indigo-600 rounded-xl items-center justify-center text-white font-bold shadow ${logoUrl ? 'hidden' : 'flex'}`}
                  >
                    {displayName.charAt(0) || 'P'}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{displayName}</p>
                    <p className="text-indigo-600 font-bold text-xs uppercase tracking-tight">
                      {getSimpleCategoryName(selectedCategory)}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-sm text-gray-600 shrink-0">Product</span>
                    <span className="text-sm font-bold text-gray-900 truncate max-w-[180px]" title={selectedProduct?.name || (isAirtime ? 'Airtime' : 'Custom Amount')}>
                      {selectedProduct?.name || (isAirtime ? 'Airtime' : 'Custom Amount')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Recipient</span>
                    <span className="text-sm font-bold text-gray-900">{accountNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Amount</span>
                    <span className="text-sm font-bold text-gray-900">{formatPrice(effectiveAmount, productCurrency)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payment Method</span>
                    <span className="text-sm font-bold text-gray-900">
                      {selectedPayment?.id === 'rewards'
                        ? 'Bonndle Rewards'
                        : selectedPayment?.id === 'bank_transfer'
                        ? 'Bank Transfer'
                        : `Card (${selectedPayment?.last4Digits || selectedPayment?.last4})`}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-gray-900">Total Due</span>
                    <span className="text-xl font-bold text-indigo-600">{formatPrice(effectiveAmount, productCurrency)}</span>
                  </div>
                  {selectedPayment?.id === 'rewards' && (
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-500">Points</span>
                      <span className="text-sm font-bold text-indigo-600">
                        {/* Calculate points using boldCreditPerNaira (matches backend calculation) */}
                        {Math.round(
                          (productCurrency === 'NGN' ? effectiveAmount : effectiveAmount * exchangeRate) * boldCreditPerNaira
                        ).toLocaleString()} Pts
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Promo/Cashback Code Input - Only show for card payments, not Bonndle Rewards */}
              {selectedPayment?.id !== 'rewards' && (
                <>
                  <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Tag size={16} className="text-indigo-600" />
                        <span className="text-sm font-bold text-gray-900">Promo | Cashback Code</span>
                      </div>
                      {hasAutoAppliedCode && promoCode && (
                        <div className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          <Sparkles size={12} />
                          <span className="text-[10px] font-bold uppercase">Auto-Applied</span>
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Enter promo or cashback code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        className={`w-full bg-gray-100 px-4 py-3 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all uppercase tracking-wider ${
                          promoCode ? 'pr-10' : ''
                        }`}
                      />
                      {promoCode && (
                        <button
                          onClick={() => setPromoCode('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {!promoCode && !hasAutoAppliedCode && (
                      <p className="text-xs text-gray-500 mt-2">
                        Have a code from a group deal? Enter it above to earn cashback.
                      </p>
                    )}
                  </div>

                  {/* Cashback Info */}
                  <div className={`p-4 rounded-xl border flex items-center gap-3 ${
                    promoCode
                      ? 'bg-indigo-50 border-indigo-200'
                      : 'bg-gray-50 border-gray-100'
                  }`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      promoCode ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}>
                      <Coins size={20} className={promoCode ? 'text-amber-400' : 'text-gray-500'} />
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${promoCode ? 'text-indigo-700' : 'text-gray-700'}`}>
                        {promoCode ? 'Cashback Rewards' : 'Cashback'}
                      </p>
                      <p className={`text-xs ${promoCode ? 'text-indigo-600' : 'text-gray-500'}`}>
                        {promoCode
                          ? `You'll save ${CASHBACK_PERCENT}% (${formatPrice(parseFloat(cashbackAmount), productCurrency)}) on this transaction`
                          : `Enter a promo code to earn ${CASHBACK_PERCENT}% cashback rewards`
                        }
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 slide-panel-footer">
              <button
                onClick={handleConfirmPayment}
                disabled={processingPayment}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processingPayment ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  `Pay ${formatPrice(effectiveAmount, productCurrency)}`
                )}
              </button>
            </div>
          </div>

          {/* Error Modal Overlay */}
          {paymentError && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
              <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-scale-in">
                {/* Error Icon */}
                <div className="bg-red-50 pt-8 pb-6 flex flex-col items-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Payment Failed</h3>
                </div>

                {/* Error Message */}
                <div className="p-6">
                  <p className="text-sm text-gray-600 text-center mb-6">
                    {paymentError}
                  </p>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => setPaymentError(null)}
                      className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold active:scale-[0.98] transition-all"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={() => setPaymentError(null)}
                      className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-bold active:scale-[0.98] transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Step: Success
  if (step === 'success') {
    const productCurrency = selectedProduct?.currency || (isGiftCard ? giftCardCurrency : provider?.currency) || 'NGN';

    return (
      <>
        {!hideBackdrop && <div className="backdrop animate-fade-in" onClick={handleClose} />}
        <div className="slide-panel">
          <div>
            <div className="slide-panel-content p-6 pt-20 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Transaction Successful</h2>
              <p className="text-gray-500 text-sm mb-6">
                Your {displayName} {getSimpleCategoryName(selectedCategory)} purchase was successful.
              </p>

              {/* Receipt Card */}
              <div className="bg-gray-50 p-4 rounded-xl text-left mb-5">
                <div className="flex justify-between items-center mb-3 gap-4">
                  <span className="text-sm text-gray-500 shrink-0">Product</span>
                  <span className="text-sm font-bold text-gray-900 truncate max-w-[180px]" title={selectedProduct?.name || (isAirtime ? 'Airtime' : 'Custom Amount')}>
                    {selectedProduct?.name || (isAirtime ? 'Airtime' : 'Custom Amount')}
                  </span>
                </div>
                {/* For rewards payment: show Product Amount; for card: show Amount Paid */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">
                    {selectedPayment?.id === 'rewards' ? 'Product Amount' : 'Amount Paid'}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{formatPrice(effectiveAmount, productCurrency)}</span>
                </div>
                {/* Cashback only applies to non-rewards (card) payments */}
                {selectedPayment?.id !== 'rewards' && parseFloat(cashbackAmount) > 0 && (
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500">Cashback Earned</span>
                    <span className="text-sm font-bold text-emerald-600">+{formatPrice(parseFloat(cashbackAmount), productCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Payment Method</span>
                  <span className="text-sm font-bold text-gray-900">
                    {selectedPayment?.id === 'rewards'
                      ? 'Bonndle Rewards'
                      : `Card (${selectedPayment?.last4Digits || selectedPayment?.last4})`}
                  </span>
                </div>
                {transactionReference && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Reference</span>
                    <span className="text-sm font-bold text-gray-900">{transactionReference}</span>
                  </div>
                )}
              </div>

              {/* Savings Banner - shows different savings based on payment method */}
              {/* For Rewards: always show (amount paid = amount saved since using points) */}
              {/* For Card: only show if cashback was earned (promo code was applied) */}
              {(selectedPayment?.id === 'rewards' || parseFloat(cashbackAmount) > 0) && (
                <div className={`px-4 py-2 rounded-full inline-flex items-center gap-2 ${
                  promoCode ? 'bg-emerald-100' : 'bg-emerald-50'
                }`}>
                  <Coins size={16} className="text-amber-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="text-emerald-700 font-bold text-sm">
                    {/* Rewards payment: show amount paid as savings; Card payment: show cashback earned */}
                    {selectedPayment?.id === 'rewards'
                      ? `You saved ${formatPrice(effectiveAmount, productCurrency)}`
                      : `You saved +${formatPrice(parseFloat(cashbackAmount), productCurrency)}`
                    }
                  </span>
                  <Coins size={16} className="text-amber-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 slide-panel-footer">
              <button
                onClick={handleClose}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg active:scale-[0.98] transition-all uppercase tracking-widest text-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}
